import { useCallback, useContext, useMemo, useState } from 'react';
import Modal from '@atlaskit/modal-dialog';
import { useNavigate } from 'react-router-dom';

import Row from 'components/Common/Row';
import Button from 'kingpin/atoms/Button';
import Form from 'kingpin/forms/Form';
import FormHeader from 'kingpin/forms/FormHeader';
import FormContent from 'kingpin/forms/FormContent';
import FormInput from '../../../../kingpin/forms/FormInput';
import TextInput, { ErrorMessage } from '../../../../kingpin/atoms/TextInput';
import Alert from '../../../../kingpin/Alert';
import EntityDefinitionsContext from '../../../../contexts/EntityDefinitionsContext';
import useUrlSlug from '../../../Settings/Advanced/AppsManager/CreateAppForm/useUrlSlug';
import getIdentifier from '../../../../getIdentifier';
import CurrentUserContext from '../../../../contexts/CurrentUserContext';
import getTimeStamp from '../../../../getTimeStamp';
import STORE from '../../../../store';
import AccountPickerContext from '../../../../contexts/AccountPickerContext';
import ToastContext from '../../../../contexts/ToastContext';
import { useEntityValues } from '../../../EntityDetailsShow/SelectedEntityProvider/useEntityOptions';
import { useBuildEntityLink } from '../../../../components/GlobalSearch/hooks/useEntityLink';

const EntityAppForm = ({
  entity,
  entityApp,
  close,
}: {
  entity: EntityDetails.Entity;
  entityApp?: EntityDetails.App;
  close: () => void;
}) => {
  const { id: currentUserId } = useContext(CurrentUserContext);
  const { selectedAccountId } = useContext(AccountPickerContext);
  const { entityDetailsApps } = useContext(EntityDefinitionsContext);
  const { showToast } = useContext(ToastContext);
  const { values: entityValues } = useEntityValues(entity);
  const buildEntityLink = useBuildEntityLink(entity);
  const navigate = useNavigate();

  const [title, setTitle] = useState<string>(entityApp ? entityApp.title : '');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const reservedUrls = useMemo(() => {
    return entityDetailsApps
      .filter((app) => {
        if (!entityApp) {
          return true;
        }

        return app.id !== entityApp.id;
      })
      .map((app) => app.urlSlug);
  }, [entityApp, entityDetailsApps]);
  const { urlSlug, onUrlSlugChanged, isUrlSlugValid } = useUrlSlug(
    entityApp ? entityApp.urlSlug : undefined,
    reservedUrls,
  );

  const draftEntityApp = useMemo((): EntityDetails.App | undefined => {
    if (!isUrlSlugValid || title === '') {
      return undefined;
    }

    return {
      id: entityApp ? entityApp.id : getIdentifier(),
      entityId: entity.id,
      title,
      tabs: entityApp ? entityApp.tabs : [],
      isHidden: entityApp ? entityApp.isHidden : false,
      urlSlug,
      createdBy: entityApp ? entityApp.createdBy : currentUserId,
      createdOn: entityApp ? entityApp.createdOn : getTimeStamp(),
      updatedBy: currentUserId,
      updatedOn: getTimeStamp(),
    };
  }, [currentUserId, entity.id, entityApp, isUrlSlugValid, title, urlSlug]);

  const onSaveClicked = useCallback(() => {
    if (!draftEntityApp || isLoading) {
      return;
    }

    setIsLoading(true);
    STORE.contentDistributions
      .getEntityDetailsApps({
        accountId: selectedAccountId,
      })
      .doc(draftEntityApp.id)
      .set(draftEntityApp)
      .then(() => {
        if (!!entityApp) {
          showToast('App updated');
          close();
          return;
        }

        const setDefaultApp = !entity.defaultAppId
          ? STORE.getEntityDefinitionsRef({ accountId: selectedAccountId })
              .doc(entity.id)
              .set({ ...entity, defaultAppId: draftEntityApp.id })
          : Promise.resolve();

        setDefaultApp.then(() => {
          showToast('App Created');
          setTimeout(() => {
            // Give firestore a moment to load it up
            const link = buildEntityLink(entityValues[0], draftEntityApp);
            if (link) {
              navigate(link);
            }
            setIsLoading(false);
          }, 1500);
        });
      });
  }, [
    buildEntityLink,
    close,
    draftEntityApp,
    entity,
    entityApp,
    entityValues,
    isLoading,
    navigate,
    selectedAccountId,
    showToast,
  ]);

  const urlInputState = useMemo(() => {
    if (isLoading) {
      return undefined;
    }

    return urlSlug !== '' && !isUrlSlugValid ? 'Error' : undefined;
  }, [isLoading, isUrlSlugValid, urlSlug]);

  const isErrorMessageVisible = useMemo(() => {
    if (isLoading) {
      return false;
    }

    return urlSlug !== '' && !isUrlSlugValid;
  }, [isLoading, isUrlSlugValid, urlSlug]);

  return (
    <Modal onClose={close} shouldScrollInViewport={false} autoFocus={false}>
      <Form>
        <FormHeader
          title={`${entityApp ? 'Edit' : 'Create'} entity app`}
          onClose={close}
        />
        <FormContent>
          <FormInput label={'Title'}>
            <TextInput
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              data-testid="entity-app-title"
            />
          </FormInput>
          <FormInput label={'URL Slug'}>
            <TextInput
              value={urlSlug}
              onChange={onUrlSlugChanged}
              state={urlInputState}
              data-testid="entity-app-url-slug"
            />
            {isErrorMessageVisible && (
              <ErrorMessage
                message={
                  'This url is already in use or contains invalid characters'
                }
              />
            )}
            {entityApp !== undefined && (
              <div style={{ marginTop: 8 }}>
                <Alert
                  type={'Warning'}
                  body={
                    'By changing this value, you will break any bookmark the users have against this asset'
                  }
                />
              </div>
            )}
          </FormInput>
        </FormContent>
        <Row spaceBetween>
          <Button
            size={'Medium'}
            type={'Secondary'}
            label={'Close'}
            onClick={close}
          />
          <Button
            size={'Medium'}
            type={'Primary'}
            onClick={onSaveClicked}
            isDisabled={draftEntityApp === undefined || isLoading}
            isLoading={isLoading}
            label={entityApp ? 'Update' : 'Create'}
          />
        </Row>
      </Form>
    </Modal>
  );
};

export default EntityAppForm;
